import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, Plus, Receipt, Sparkles, Users } from "lucide-react";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Badge, Btn, Empty, Loading, Page, SearchBox, StatusPill, cx } from "../components/wf";
import { api, budgetLevel, budgetTone, money, num, progressOf, quarters } from "../lib/api";
import { isManagerUser, useAuth } from "../lib/auth";
import { useStatuses } from "../lib/queries";
import { DueSoon, WeekRecap } from "./MyWork";

const isPaid = (o) => o.status.trim().toLowerCase() === "paid";

function StatusFilter({ options, selected, onChange, counts }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="orders-status-filter">
      <button onClick={() => onChange([])} className={cx("rounded-full px-3 py-1.5 text-xs font-bold", !selected.length ? "bg-wf-ink text-white" : "bg-wf-s3 text-wf-ink2")} data-testid="status-filter-all">
        All statuses · {counts["All"] ?? 0}
      </button>
      {options.map((s) => {
        const on = selected.includes(s);
        return (
          <button key={s} data-testid={`status-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => onChange(on ? selected.filter((x) => x !== s) : [...selected, s])}
            className={cx("rounded-full px-3 py-1.5 text-xs font-bold transition-colors", on ? "bg-wf-brand text-wf-onbrand" : "bg-wf-s3 text-wf-ink2 hover:bg-wf-soft")}>
            {s} · {counts[s] ?? 0}
          </button>
        );
      })}
    </div>
  );
}

function JobCard({ item, i }) {
  const pct = item.totals.budget_used_pct;
  const level = budgetLevel(pct);
  const overdue = item.project_overdue ?? 0;
  const work = item.entries?.work ?? [];
  const done = work.reduce((n, w) => n + progressOf(w), 0);
  return (
    <Link to={`/work-orders/${item.id}`} data-testid={`work-order-card-${item.id}`} style={{ animationDelay: `${Math.min(i, 10) * 30}ms` }}
      className="wf-rise group flex flex-col gap-2 rounded-2xl border border-wf-line bg-wf-surface p-5 transition-[border-color,transform,box-shadow] hover:-translate-y-0.5 hover:border-wf-brand2 hover:shadow-lg hover:shadow-black/5">
      <div className="flex items-start justify-between gap-3">
        <p className="truncate text-[17px] font-bold">{item.customer?.name ?? "Unknown customer"}</p>
        <div className="flex max-w-[50%] flex-col items-end gap-1">
          <StatusPill label={item.status} />
          {!!item.customer?.kastle_customer_number && <span className="truncate text-xs font-semibold text-wf-mute">Kastle # {item.customer.kastle_customer_number}</span>}
        </div>
      </div>
      {(item.is_new || pct != null || overdue > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {item.is_new && <Badge label="NEW" tone="new" testid={`new-badge-${item.id}`} />}
          {overdue > 0 && <Badge label={`${overdue} overdue item${overdue === 1 ? "" : "s"}`} tone="over" />}
          {pct != null && <Badge tone={budgetTone(pct)} label={level === "over" ? `Over budget · ${Math.round(pct)}%` : `${Math.round(pct)}% of budget`} />}
        </div>
      )}
      <p className="line-clamp-2 text-sm text-wf-ink2">{item.job_description || "No job description"}</p>
      <div className="mt-1 flex flex-wrap gap-4 text-[13px] font-semibold text-wf-mute">
        <span className="flex items-center gap-1"><Clock size={14} />{num(item.totals.total_hours)} hrs</span>
        <span className="flex items-center gap-1"><Users size={14} />{num(item.totals.man_hours)} man hrs</span>
        {item.totals.total_expenses != null && <span className="flex items-center gap-1"><Receipt size={14} />{money(item.totals.total_expenses)}</span>}
      </div>
      {work.length > 0 && (
        <p className="mt-1 flex items-center gap-1 text-[13px] font-bold text-green-600" data-testid={`work-progress-${item.id}`}>
          <CheckCircle2 size={15} /> {quarters(done)} of {work.length} completed
        </p>
      )}
    </Link>
  );
}

/** Jobs list. `completed` shows only Paid jobs; otherwise Paid jobs are hidden. */
export default function Jobs({ completed = false }) {
  const { user } = useAuth();
  const isManager = isManagerUser(user);
  const nav = useNavigate();
  const [mine, setMine] = useState(!isManager);
  const [filter, setFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [alert, setAlert] = useState(null);
  const orders = useQuery({ queryKey: ["work-orders", { mine }], queryFn: () => api(`/work-orders?mine=${mine}`) });
  const dash = useQuery({ queryKey: ["dashboard", { mine }], queryFn: () => api(`/dashboard?mine=${mine}`) });
  const statuses = useStatuses();

  const scoped = useMemo(() => (orders.data ?? []).filter((o) => isPaid(o) === completed), [orders.data, completed]);
  const counts = useMemo(() => {
    const c = { All: scoped.length };
    scoped.forEach((o) => o.status && (c[o.status] = (c[o.status] ?? 0) + 1));
    return c;
  }, [scoped]);
  const data = useMemo(() => {
    const q = search.toLowerCase();
    return scoped
      .filter((o) => (!filter.length || filter.includes(o.status)) && (alert !== "new" || o.is_new) && (alert !== "budget" || !!budgetLevel(o.totals.budget_used_pct)) &&
        (!q || o.customer?.name.toLowerCase().includes(q) || o.job_description.toLowerCase().includes(q)))
      .sort((a, b) => Number(!!b.is_new) - Number(!!a.is_new));
  }, [scoped, filter, search, alert]);
  const d = dash.data;

  return (
    <Page title={completed ? "Completed Jobs" : "Jobs"} subtitle={completed ? "Jobs marked Paid" : `${scoped.length} ${scoped.length === 1 ? "job" : "jobs"}`} wide testid={completed ? "completed-jobs-page" : "jobs-page"}
      right={!completed && <Btn icon={Plus} onClick={() => nav("/work-orders/new")} data-testid="add-work-order-button">New Job</Btn>}>
      {!completed && <div className="mb-6 grid gap-4 lg:grid-cols-2"><DueSoon /><WeekRecap /></div>}
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        {isManager && (
          <div className="flex rounded-xl bg-wf-s3 p-1">
            {[true, false].map((m) => (
              <button key={String(m)} data-testid={m ? "orders-toggle-mine" : "orders-toggle-all"} onClick={() => setMine(m)}
                className={cx("h-9 rounded-lg px-5 text-sm font-semibold transition-colors", mine === m ? "bg-wf-surface text-wf-ink shadow-sm" : "text-wf-mute")}>
                {m ? "My Jobs" : "All Jobs"}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1"><SearchBox testid="orders-search-input" value={search} onChange={setSearch} placeholder="Search customer or job" /></div>
      </div>
      {!completed && (
        <StatusFilter options={(statuses.data ?? []).map((s) => s.name).filter((n) => n.trim().toLowerCase() !== "paid")} selected={filter} onChange={setFilter} counts={counts} />
      )}
      {!completed && (!!d?.new_assigned || !!d?.budget_alerts) && (
        <div className="mt-5 flex flex-wrap gap-3">
          {!!d.new_assigned && (
            <button data-testid="alert-new-jobs" onClick={() => setAlert(alert === "new" ? null : "new")}
              className={cx("flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold", alert === "new" ? "border-wf-brand bg-wf-soft" : "border-wf-line bg-wf-s2")}>
              <Sparkles size={17} className="text-wf-brand" /> {d.new_assigned} new job{d.new_assigned > 1 ? "s" : ""} for you
            </button>
          )}
          {!!d.budget_alerts && (
            <button data-testid="alert-budget-jobs" onClick={() => setAlert(alert === "budget" ? null : "budget")}
              className={cx("flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold", alert === "budget" ? "border-wf-brand bg-wf-soft" : "border-wf-line bg-wf-s2")}>
              <AlertTriangle size={17} className="text-wf-warn" /> {d.budget_alerts} near/over budget
            </button>
          )}
        </div>
      )}
      <p className="mb-3 mt-6 text-[13px] font-bold uppercase tracking-[0.08em] text-wf-mute" data-testid="jobs-count">
        {completed ? "Paid" : mine ? "My Jobs" : "All Jobs"} · {data.length}{alert ? (alert === "new" ? " · New only" : " · Budget alerts") : ""}
      </p>
      {orders.isLoading ? <Loading /> : data.length === 0 ? (
        orders.isError ? <Empty title="Couldn't load jobs" subtitle={orders.error?.message} /> :
          <Empty title={completed ? "No paid jobs yet" : mine ? "No jobs assigned to you" : "No jobs yet"}
            subtitle={completed ? "Jobs move here when their status is set to Paid." : !isManager ? "Jobs assigned to you show up here." : mine ? "Switch to All Jobs to see everything." : "Click New Job to create your first job."} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="work-orders-list">
          {data.map((o, i) => <JobCard key={o.id} item={o} i={i} />)}
        </div>
      )}
    </Page>
  );
}
